import React, { useState, useEffect } from 'react';
import ChildHeader from '../../Dashboard/Main/ChildHeader';
import { Text, Button, VStack, Heading, Stack, chakra, Center, Alert, AlertIcon, Box } from '@chakra-ui/react';
import BottomNavBar from 'components/BottomNavBar';
import { useLocation } from 'react-router-dom';
import { MdCreditCard } from 'react-icons/md';
import { api } from 'api/api';

const ENDPOINT =
    process.env.REACT_APP_ENVIRONMENT === 'production'
        ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
        : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

function DetalhesAnaliseSistema() {
    const { state } = useLocation();
    const { atendimento } = state;
    const [atendente, setAtendente] = useState([]);

    function myToLowerCase(text) {
        if (typeof text === 'string') {
            return text.toLocaleLowerCase();
        } else {
            return text;
        }
    }

    const parseJsonArray = (value) => {
        if (Array.isArray(value)) return value;
        if (!value || typeof value !== 'string') return [];

        try {
            const parsedValue = JSON.parse(value);
            return Array.isArray(parsedValue) ? parsedValue : [];
        } catch {
            return [];
        }
    };

    const parseDateValue = (value) => {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

        if (typeof value === 'string') {
            const [datePart] = value.split(/[T ]/);
            const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);

            if (match) {
                const [, year, month, day] = match;
                return new Date(Number(year), Number(month) - 1, Number(day));
            }
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const formatDateForDisplay = (value) => {
        const date = parseDateValue(value);

        if (!date) return null;

        const weekDay = date.toLocaleDateString('pt-br', { weekday: 'long' });
        const dateText = date.toLocaleDateString('pt-br', { day: '2-digit', month: '2-digit', year: 'numeric' });

        return `${weekDay.charAt(0).toUpperCase() + weekDay.slice(1)}, ${dateText}`;
    };

    const formatTimeForDisplay = (value) => {
        if (!value) return null;

        const [hour, minute] = String(value).split(':');

        if (!hour || !minute) return String(value);

        return `${hour}:${minute}h`;
    };

    const getPeriodForDisplay = (value) => {
        if (!value) return '';

        const [hour, minute = '0'] = String(value).split(':');
        const hourNumber = Number(hour);
        const minuteNumber = Number(minute);

        if (Number.isNaN(hourNumber) || Number.isNaN(minuteNumber)) return '';

        return hourNumber * 60 + minuteNumber > 12 * 60 ? 'Tarde' : 'Manhã';
    };

    const getDateKey = (value) => {
        const date = parseDateValue(value);

        if (!date) return '9999-99-99';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const isTruthy = (value) => {
        return value === true || value === 'true' || value === 1 || value === '1';
    };

    const splitProcedureNameStatus = (procedure) => {
        const [procedureName, procedureStatus] = String(procedure).split(' -> ');

        return {
            procedureName: procedureName?.trim(),
            procedureStatus: procedureStatus?.trim(),
        };
    };

    const getProcedureDetailsList = () => {
        return parseJsonArray(atendimento?.DADOS_GERAIS_EXAMES);
    };

    const getProcedureDetails = (procedureName, index) => {
        const details = getProcedureDetailsList();
        const procedureScheduleId = parseJsonArray(atendimento?.agenda_id_multiprocedimento)[index];
        const detailByScheduleId = details.find((procedure) => String(procedure?.agenda_exames_id || '') === String(procedureScheduleId || ''));

        return (
            detailByScheduleId ||
            details.find(
                (procedure) =>
                    String(procedure?.nome || '')
                        .trim()
                        .toLowerCase() ===
                    String(procedureName || '')
                        .trim()
                        .toLowerCase(),
            ) ||
            details[index] ||
            null
        );
    };

    const getProcedureScheduleParts = (procedureDetails, useAttendanceFallback = true) => {
        const dateValue = procedureDetails?.dia_selecionado || procedureDetails?.APP_DATE || (useAttendanceFallback ? atendimento?.APP_DATE : null);
        const dateText = formatDateForDisplay(dateValue);
        const hourValue =
            procedureDetails?.horario ||
            procedureDetails?.hora_selecionada_display ||
            procedureDetails?.START_TIME ||
            (useAttendanceFallback ? atendimento?.START_TIME : null);
        const hourText = formatTimeForDisplay(hourValue);
        const isByOrder = isTruthy(procedureDetails?.agenda_ordem_chegada) || isTruthy(procedureDetails?.isByOrder) || isTruthy(atendimento?.ordem_chegada);
        const periodText = getPeriodForDisplay(hourValue);
        const detailText = isByOrder
            ? `${periodText ? `${periodText} — ` : ''}Ordem de chegada`
            : hourText
                ? `${periodText ? `${periodText} — ` : ''}${hourText}`
                : null;

        return {
            dateText,
            detailText,
            dateKey: getDateKey(dateValue),
            sortTime: hourValue || '99:99',
            scheduleKey: isByOrder ? `order-${periodText || 'periodo'}` : hourValue || 'sem-horario',
        };
    };

    const getProcedureScheduleText = (procedureDetails, useAttendanceFallback = true) => {
        const { dateText, detailText } = getProcedureScheduleParts(procedureDetails, useAttendanceFallback);

        if (dateText && detailText) return `${dateText}\n${detailText}`;
        if (dateText) return dateText;
        if (detailText) return detailText;

        return 'Data e horário não informados';
    };

    const isBlankDoctorName = (doctorName) => {
        const normalizedDoctorName = String(doctorName || '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();

        return !normalizedDoctorName || normalizedDoctorName === 'sem solicitante';
    };

    const getProcedureGroups = (procedureItems) => {
        const groups = new Map();

        procedureItems.forEach((procedureItem, index) => {
            const isDoctorDependent = !isBlankDoctorName(procedureItem.doctorName) || Boolean(procedureItem.doctorId);
            const doctorDisplayName = isDoctorDependent
                ? isBlankDoctorName(procedureItem.doctorName)
                    ? 'Profissional não informado'
                    : procedureItem.doctorName
                : '';
            const doctorKey = isDoctorDependent ? procedureItem.doctorId || doctorDisplayName || `medico-nao-informado-${index}` : 'sem-medico';
            const groupKey = isDoctorDependent
                ? `${procedureItem.schedule.dateKey}|medico|${doctorKey}|${procedureItem.schedule.scheduleKey}`
                : `${procedureItem.schedule.dateKey}|sem-medico|${procedureItem.schedule.scheduleKey}`;

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    key: groupKey,
                    dateText: procedureItem.schedule.dateText,
                    detailText: procedureItem.schedule.detailText,
                    dateKey: procedureItem.schedule.dateKey,
                    sortTime: procedureItem.schedule.sortTime,
                    firstIndex: index,
                    doctorDisplayName,
                    isDoctorDependent,
                    procedures: [],
                });
            }

            groups.get(groupKey).procedures.push(procedureItem);
        });

        return Array.from(groups.values()).sort((a, b) => {
            if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey);
            if (a.isDoctorDependent !== b.isDoctorDependent) return a.isDoctorDependent ? 1 : -1;
            if (a.sortTime !== b.sortTime) return a.sortTime.localeCompare(b.sortTime);
            return a.firstIndex - b.firstIndex;
        });
    };

    const procedureNames = Array.isArray(atendimento?.PROCEDURE_NAMES) ? atendimento.PROCEDURE_NAMES : [];
    const showProcedureDates = procedureNames.length > 1;
    const procedureItems = procedureNames.map((procedure, index) => {
        const { procedureName, procedureStatus } = splitProcedureNameStatus(procedure);
        const procedureDetails = getProcedureDetails(procedureName, index);

        return {
            itemKey: `${procedureName}-${index}`,
            doctorId: procedureDetails?.medico_id,
            doctorName: String(procedureDetails?.medico_nome || '').trim(),
            procedureName,
            procedureStatus,
            schedule: getProcedureScheduleParts(procedureDetails, !showProcedureDates),
        };
    });
    const procedureGroups = showProcedureDates ? getProcedureGroups(procedureItems) : [];
    const shouldShowDoctorInProcedureGroups = procedureGroups.some((group) => group.doctorDisplayName);
    const resourceDoctorName = String(atendimento?.RESOURCE_NAME || '').trim();
    const cardDoctorText = shouldShowDoctorInProcedureGroups || isBlankDoctorName(resourceDoctorName) ? '' : 'Dr. ' + myToLowerCase(resourceDoctorName);

    const renderProcedureItem = ({ itemKey, procedureName, procedureStatus }) => (
        <Box key={itemKey} mb="0.5">
            <Text fontWeight="bold" fontSize="0.8em">
                {procedureName}
            </Text>
            {procedureStatus === 'FINALIZADO' ? (
                <Text color="teal" fontSize="0.8em" fontWeight="semibold">
                    Realizado
                </Text>
            ) : (
                <></>
            )}
        </Box>
    );

    const useListProcedures = showProcedureDates
        ? procedureGroups.map((group, index) => (
            <Box
                key={group.key}
                pt={index === 0 ? 0 : 2}
                mt={index === 0 ? 0 : 2}
                borderTop={index === 0 ? 'none' : '1px solid'}
                borderColor="gray.100"
            >
                {group.doctorDisplayName ? (
                    <Text fontSize="0.78em" fontWeight="semibold" color="gray.600" mb="1" casing="capitalize">
                        {myToLowerCase(group.doctorDisplayName)}
                    </Text>
                ) : (
                    <></>
                )}
                {group.procedures.map(renderProcedureItem)}
                <Box mt="1">
                    <Text color="gray.500" fontSize="0.8em" fontWeight="semibold">
                        {group.dateText || 'Data não informada'}
                    </Text>
                    {group.detailText ? (
                        <Text color="gray.500" fontSize="0.8em" fontWeight="semibold">
                            {group.detailText}
                        </Text>
                    ) : (
                        <></>
                    )}
                </Box>
            </Box>
        ))
        : procedureItems.map(renderProcedureItem);

    const fetchOperadorAtendimento = async (atendimento_id) => {
        const url = `${ENDPOINT}/appointments/fetchOperadorAtendimento/${atendimento_id}`;
        const response = await api(url, {
            headers: { 'Content-Type': 'application/json' },
            method: 'GET',
        });
        const postsData = await response.json();
        if (postsData) {
            setAtendente(postsData);
        }
    };

    useEffect(() => {
        fetchOperadorAtendimento(atendimento.APP_LID)
    }, [atendimento]);

console.log('atendimento', atendimento.APP_LID)
    return (
        <>
            <ChildHeader />
            <VStack
                align="center"
                justify={{
                    base: 'center',
                    md: 'space-around',
                    xl: 'space-between',
                }}
                direction={{ base: 'column-reverse' }}
                wrap="no-wrap"
                spacing={5}
                px={4}
                py={6}
            >
                <Stack direction="row" align="center">
                    <Heading fontSize="lg">Processando Pagamento</Heading>
                    <MdCreditCard size="50" />
                </Stack>
                <Center>
                    {' '}
                    <Text alignItems="center">O pagamento de: </Text>
                </Center>

                <Center>
                    <Stack
                        borderWidth="1px"
                        borderRadius="25px"
                        minW="100%"
                        maxW="500px"
                        direction={{ base: 'column', md: 'row' }}
                        bg={'white'}
                        boxShadow={'lg'}
                        padding={4}
                    >
                        <VStack alignItems="right" spacing={4}>
                            <Heading fontSize="xs">
                                {atendimento.financeiro_faturas_pendentes_app_id
                                    ? 'Agendamento de exames'
                                    : state.atendimento?.ACTIVITY_NAME
                                        ? state.atendimento?.ACTIVITY_NAME
                                        : 'CONSULTA COM ' + state.atendimento?.ESPECIALIDADE?.toUpperCase()}
                            </Heading>
                            {cardDoctorText ? (
                                <Text fontSize="0.8em" fontWeight="semibold" casing="capitalize">
                                    {cardDoctorText}
                                </Text>
                            ) : (
                                <></>
                            )}
                            <chakra.p fontSize="0.7em">
                                <chakra.span fontWeight="bold"> Modalidade: </chakra.span>
                                {state.atendimento.atendimento_telemed === true ? 'Telemedicina' : 'Presencial'}
                            </chakra.p>
                            {!showProcedureDates ? (
                                <chakra.p fontSize="0.8em" whiteSpace="pre-line">
                                    <chakra.span fontWeight="bold">Data do atendimento: </chakra.span>
                                    {getProcedureScheduleText(getProcedureDetailsList()[0] || null)}
                                </chakra.p>
                            ) : (
                                <></>
                            )}
                            {procedureNames.length > 0 ? (
                                <Box pt="2" borderTop="1px solid" borderColor="gray.100">
                                    <chakra.p fontSize="0.8em" mb="1">
                                        <chakra.span fontWeight="bold">Procedimento{procedureNames.length > 1 ? 's' : ''}: </chakra.span>
                                    </chakra.p>
                                    <Box>{useListProcedures}</Box>
                                </Box>
                            ) : (
                                <></>
                            )}
                        </VStack>
                    </Stack>
                </Center>

                <chakra.span textAlign="center" fontSize="1em" fontWeight="bold" >
                    {' '}
                    <Text alignItems="center">Está em processamento. Entre em contato com o(a) operador {atendente.nome} ou com o suporte para que seu agendamento seja faturado. </Text>
                </chakra.span>

                <chakra.span textAlign="center" fontSize="xs" fontWeight="bold" color="yellow.500"></chakra.span>
                <Stack>
                    <Alert status="info" fontSize="xs" borderRadius={20}>
                        <AlertIcon />
                        <Text noOfLines={1} fontSize="10">
                            Após a confirmação do pagamento você será notificado!
                        </Text>
                    </Alert>
                </Stack>
                <chakra.span textAlign="center" fontSize="xs" fontWeight="bold" color="red">
                    Caso o pagamento não seja realizado no prazo estipulado a sua aquisição será cancelada.
                </chakra.span>
            </VStack>
            <VStack mt="3" mb="15%">
                <Stack direction="column" spacing={2}>
                    <Text textAlign="center" fontSize="0.9em">
                        Problemas com seu agendamento?
                    </Text>
                    <Button
                        colorScheme="teal"
                        borderRadius={8}
                        height="40px"
                        onClick={() => {
                            window?.ReactNativeWebView?.postMessage(
                                JSON.stringify({
                                  service: true,
                                  openInBrowser: true,
                                  url: 'https://wa.me/5508002802133',
                                })
                            );
                        }}
                        width="350px"
                        variant="ghost"
                    >
                        Falar com Suporte
                    </Button>
                </Stack>
            </VStack>
            <BottomNavBar />
        </>
    )
}

export default DetalhesAnaliseSistema
